import { Checkbox, FormControl, FormControlLabel, FormHelperText, Switch, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useErrorHandler } from "../../../common/hooks";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { CheckboxStyle, InputCheckboxModel } from "../../Models/InputCheckboxModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const InputCheckboxComponent: React.FC<IComponentProps<InputCheckboxModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();

  const label = useObservedText(component.Label, context, "InputCheckboxComponent");
  const description = useObservedText(component.Description, context, "InputCheckboxComponent");

  const [checked, setChecked] = useState<boolean>(false);
  const [valueResult] = useObservedExpression<boolean>(component.Value, context, "InputCheckboxComponent");
  const [disabled] = useObservedExpression<boolean | undefined>(component.Disabled, context, "InputCheckboxComponent");
  const { isVisible, hiddenSx } = useComponentVisibility(component, context, "InputCheckboxComponent");

  useEffect(() => {
    if (valueResult !== undefined && valueResult !== checked) {
      setChecked(!!valueResult);
    }
  }, [valueResult]);

  // Apply default value when no value is set
  useEffect(() => {
    const applyDefaultValue = async () => {
      if (!component.Value || !component.DefaultValue) return;

      try {
        const current = await context.Evaluator.evaluate(component.Value, context);
        if (current === undefined || current === null) {
          const defaultVal = await context.Evaluator.evaluate(component.DefaultValue, context);
          if (defaultVal !== undefined && defaultVal !== null) {
            context.Evaluator.assign(component.Value, context, !!defaultVal);
            setChecked(!!defaultVal);
          }
        }
      } catch (error) {
        errorHandler(error);
      }
    };

    applyDefaultValue();
  }, []);

  const assignValue = (newValue: boolean) => {
    setChecked(newValue);

    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, newValue);
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    assignValue(event.target.checked);
  };

  const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue !== null) {
      assignValue(newValue);
    }
  };

  const componentLayoutSx = getComponentLayout(component);

  const selectionProps = useComponentSelection({ component, componentPath });

  const isDisabled = disabled === true;

  if (!isVisible) return null;

  const checkboxStyle = component.Style ?? CheckboxStyle.Checkbox;
  const control =
    checkboxStyle === CheckboxStyle.Switch ? (
      <Switch checked={checked} onChange={handleInputChange} disabled={isDisabled} />
    ) : checkboxStyle === CheckboxStyle.Toggle ? (
      <ToggleButtonGroup exclusive size="small" value={checked} onChange={handleToggleChange} disabled={isDisabled} aria-label={label}>
        <ToggleButton value={true}>On</ToggleButton>
        <ToggleButton value={false}>Off</ToggleButton>
      </ToggleButtonGroup>
    ) : (
      <Checkbox checked={checked} onChange={handleInputChange} disabled={isDisabled} />
    );

  return (
    <FormControl sx={{ ...componentLayoutSx, ...hiddenSx }} {...selectionProps}>
      <FormControlLabel label={label} control={control} title={component.DisabledTooltip} />
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};

export default InputCheckboxComponent;
